import React from 'react';
import {
  cloneDeep,
  get,
  filter,
  map,
  reduce,
  assign,
  flatten,
  without
} from 'lodash';

import MdAccessTime from '@material-ui/icons/AccessTime';
import MdArrowDropDown from '@material-ui/icons/ArrowDropDown';
import MdCheckBox from '@material-ui/icons/CheckBox';
import MdClearAll from '@material-ui/icons/ClearAll';
import MdColorLens from '@material-ui/icons/ColorLens';
import MdDateRange from '@material-ui/icons/DateRange';
import MdDehaze from '@material-ui/icons/Dehaze';
import MdEdit from '@material-ui/icons/Edit';
import MdEmail from '@material-ui/icons/Email';
import MdFiberSmartRecord from '@material-ui/icons/FiberSmartRecord';
import MdImage from '@material-ui/icons/Image';
import MdLabel from '@material-ui/icons/Label';
import MdLink from '@material-ui/icons/Link';
import MdLocationOn from '@material-ui/icons/LocationOn';
import MdLock from '@material-ui/icons/Lock';
import MdLooks5 from '@material-ui/icons/Looks5';
import MdOndemandVideo from '@material-ui/icons/OndemandVideo';
import MdRadioButtonChecked from '@material-ui/icons/RadioButtonChecked';
import MdRepeat from '@material-ui/icons/Repeat';
import MdStar from '@material-ui/icons/Star';
import MdTextFields from '@material-ui/icons/TextFields';
import MdTextFormat from '@material-ui/icons/TextFormat';
import MdTune from '@material-ui/icons/Tune';

import getValidators from './validators';
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_TIME_FORMAT,
  SUPPORTED_SEPARATORS,
  DEFAULT_SEPARATOR
} from '../../../config/constants';

export const baseFields = [
  {
    type: 'text',
    title: 'Field Name',
    key: 'title',
    defaultValue: 'Field',
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    },
    constraints: {
      presence: {
        message: '^Title is required',
        allowEmpty: false
      }
    }
  },
  {
    type: 'linked-text',
    title: 'Field Key',
    key: 'key',
    linkedField: 'title',
    transformFunction: 'camelCase',
    manualOverwrite: true,
    defaultValue() {
      return `field_${Date.now()}`;
    },
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    },
    constraints: {
      presence: {
        message: '^Field key is required',
        allowEmpty: false
      },
      format: {
        pattern: '[a-z0-9_-]+',
        flags: 'i',
        message: 'Can only be a valid slug'
      }
    }
  },
  {
    type: 'text',
    title: 'Description',
    key: 'description',
    defaultValue: '',
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    }
  },
  {
    type: 'text',
    title: 'Default Value',
    key: 'defaultValue',
    defaultValue: '',
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    }
  },
  {
    type: 'select',
    title: 'Layout (Large)',
    key: 'gridColumns.lg',
    defaultValue: 12,
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    },
    options: [
      {
        value: 12,
        label: '1/1'
      },
      {
        value: 3,
        label: '1/4'
      },
      {
        value: 4,
        label: '1/3'
      },
      {
        value: 6,
        label: '1/2'
      },
      {
        value: 9,
        label: '3/4'
      },
      {
        value: 8,
        label: '2/3'
      }
    ]
  },
  {
    type: 'select',
    title: 'Layout (Medium)',
    key: 'gridColumns.md',
    defaultValue: 12,
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    },
    options: [
      {
        value: 12,
        label: '1/1'
      },
      {
        value: 3,
        label: '1/4'
      },
      {
        value: 4,
        label: '1/3'
      },
      {
        value: 6,
        label: '1/2'
      },
      {
        value: 9,
        label: '3/4'
      },
      {
        value: 8,
        label: '2/3'
      }
    ]
  },
  {
    type: 'select',
    title: 'Layout (Small)',
    key: 'gridColumns.sm',
    defaultValue: 12,
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    },
    options: [
      {
        value: 12,
        label: '1/1'
      },
      {
        value: 3,
        label: '1/4'
      },
      {
        value: 4,
        label: '1/3'
      },
      {
        value: 6,
        label: '1/2'
      },
      {
        value: 9,
        label: '3/4'
      },
      {
        value: 8,
        label: '2/3'
      }
    ]
  },
  {
    type: 'select',
    title: 'Layout (Extra Small)',
    key: 'gridColumns.xs',
    defaultValue: 12,
    size: {
      xs: 12,
      sm: 12,
      md: 6,
      lg: 6
    },
    options: [
      {
        value: 12,
        label: '1/1'
      },
      {
        value: 3,
        label: '1/4'
      },
      {
        value: 4,
        label: '1/3'
      },
      {
        value: 6,
        label: '1/2'
      },
      {
        value: 9,
        label: '3/4'
      },
      {
        value: 8,
        label: '2/3'
      }
    ]
  },
  {
    type: 'switch',
    title: 'Show in overview table',
    key: 'show',
    defaultValue: false,
    size: {
      xs: 12,
      sm: 12,
      md: 4,
      lg: 4
    }
  },
  {
    type: 'repeater',
    title: 'Validation Rules',
    key: 'constraints',
    layout: 'list',
    defaultValue: [],
    options: [
      {
        key: 'rule',
        title: 'Rule',
        type: 'select',
        constraints: {
          presence: {
            message: '^Validation rule is required',
            allowEmpty: false
          }
        },
        options: getValidators().map(validator => ({
          value: validator.key,
          label: validator.title
        }))
      },
      {
        key: 'ruleValue',
        title: 'Rule Options',
        type: 'fieldset',
        defaultValue: {},
        options: [] // Options are dynamically set based on selected "rule"
      }
    ]
  }
];

export const formFields = [
  [
    {
      type: 'boolean',
      name: 'True / False',
      icon: () => <MdCheckBox />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'boolean';
        return [...extendedBaseFields];
      }
    },
    {
      type: 'checkbox',
      name: 'Checkbox',
      icon: () => <MdCheckBox />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'checkbox';
        return [...extendedBaseFields];
      }
    },
    {
      type: 'switch',
      name: 'Switch',
      icon: () => <MdFiberSmartRecord />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'switch';
        return [...extendedBaseFields];
      }
    },
    {
      type: 'date',
      name: 'Date',
      icon: () => <MdDateRange />,
      fields({ props, state }) {
        const extendedBaseFields = cloneDeep(baseFields);
        const displayFormat = get(
          props,
          'firebase.settings.globals.dateFormat',
          DEFAULT_DATE_FORMAT
        );
        extendedBaseFields[3].type = 'date';
        extendedBaseFields[3].displayFormat = get(
          state,
          'editing.displayFormat',
          displayFormat
        );
        extendedBaseFields[8].size = {
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6
        };

        extendedBaseFields.splice(9, 0, {
          type: 'text',
          title: 'Display Format',
          key: 'displayFormat',
          defaultValue: displayFormat,
          size: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          },
          constraints: {
            presence: {
              message: '^Field is required',
              allowEmpty: false
            }
          }
        });

        return [...extendedBaseFields];
      }
    },
    {
      type: 'time',
      name: 'Time',
      icon: () => <MdAccessTime />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'time';
        return [...extendedBaseFields];
      }
    },
    {
      type: 'datetime-local',
      name: 'Date-Time Local',
      icon: () => <MdDateRange />,
      fields({ props, state }) {
        const extendedBaseFields = cloneDeep(baseFields);
        const dateFormat = get(props, 'firebase.settings.globals.dateFormat');
        const timeFormat = get(props, 'firebase.settings.globals.timeFormat');
        const displayFormat =
          dateFormat && timeFormat
            ? `${dateFormat} ${timeFormat}`
            : DEFAULT_DATE_TIME_FORMAT;
        extendedBaseFields[3].type = 'datetime-local';
        extendedBaseFields[3].displayFormat = get(
          state,
          'editing.displayFormat',
          displayFormat
        );
        extendedBaseFields[8].size = {
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6
        };

        extendedBaseFields.splice(9, 0, {
          type: 'text',
          title: 'Display Format',
          key: 'displayFormat',
          defaultValue: displayFormat,
          size: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          },
          constraints: {
            presence: {
              message: '^Field is required',
              allowEmpty: false
            }
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'email',
      name: 'Email',
      icon: () => <MdEmail />,
      fields: () => [...baseFields]
    },
    {
      type: 'color',
      name: 'Color Picker',
      icon: () => <MdColorLens />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'color';
        return [...extendedBaseFields];
      }
    },
    {
      type: 'media',
      name: 'Media',
      icon: () => <MdOndemandVideo />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3] = {
          type: 'number',
          title: 'Selection Limit',
          key: 'limit',
          defaultValue: 1,
          size: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          }
        };
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Media Types',
          key: 'mediaTypes',
          multiple: true,
          defaultValue: null,
          options: [
            {
              value: 'files',
              label: 'Files'
            },
            {
              value: 'images',
              label: 'Images'
            }
          ],
          constraints: {
            presence: {
              message: '^Need to select at least one media type',
              allowEmpty: false
            }
          },
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'location',
      name: 'Location',
      icon: () => <MdLocationOn />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'location';
        extendedBaseFields[3].config = {
          GOOGLE_MAPS_API_KEY: 'AIzaSyCoq4_-BeKtYRIs-3FjJL721G1eP5DaU0g'
        };
        extendedBaseFields[3].size = {
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6
        };
        extendedBaseFields.splice(4, 0, {
          type: 'text',
          title: 'Google Maps API Key',
          key: 'config.GOOGLE_MAPS_API_KEY',
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          },
          constraints: {
            presence: {
              message: 'Field is required',
              allowEmpty: false
            }
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'password',
      name: 'Password',
      icon: () => <MdLock />,
      fields: () => [...baseFields]
    },
    {
      type: 'linked-text',
      name: 'Linked Text',
      icon: () => <MdLink />,
      fields({ state }) {
        const SUPPORTED_TYPES = [
          'color',
          'date',
          'datetime-local',
          'email',
          'number',
          'password',
          'range',
          'text',
          'textarea',
          'time'
        ];

        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields.splice(4, 0, {
          type: 'switch',
          key: 'manualOverwrite',
          title: 'Allow manual overwrite',
          defaultValue: false
        });
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Transform Function',
          key: 'transformFunction',
          description: "The linked field's value will be passed to this method",
          options: [
            { value: 'camelCase', label: 'camelCase' },
            { value: 'capitalize', label: 'Capitalize' },
            { value: 'deburr', label: 'Deburr' },
            { value: 'escape', label: 'Escape' },
            { value: 'kebabCase', label: 'kebab-case' },
            { value: 'lowerCase', label: 'lowercase' },
            { value: 'lowerFirst', label: 'lOWER FIRST' },
            { value: 'snakeCase', label: 'snake_case' },
            { value: 'startCase', label: 'Start Case' },
            { value: 'toLower', label: 'to lower' },
            { value: 'toUpper', label: 'TO UPPER' },
            { value: 'trim', label: 'Trim' },
            { value: 'trimEnd', label: 'Trim End' },
            { value: 'trimStart', label: 'Trim Start' },
            { value: 'unescape', label: 'Unescape' },
            { value: 'upperCase', label: 'UPPERCASE' },
            { value: 'upperFirst', label: 'Upper first' }
          ],
          size: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          },
          constraints: {
            presence: {
              message: 'Field is required',
              allowEmpty: false
            }
          }
        });
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Linked Field',
          key: 'linkedField',
          options: map(
            filter(get(state, 'formData.fields', []), ({ type }) =>
              SUPPORTED_TYPES.includes(type)
            ),
            ({ key: value, title: label }) => ({ value, label })
          ),
          size: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          },
          constraints: {
            presence: {
              message: 'Field is required',
              allowEmpty: false
            }
          }
        });

        return [...extendedBaseFields];
      }
    },
    {
      type: 'tag',
      name: 'Tag',
      icon: () => <MdLabel />,
      fields: () => [...baseFields]
    }
  ],
  [
    {
      type: 'text',
      name: 'Text',
      icon: () => <MdTextFields />,
      fields: () => [...baseFields]
    },
    {
      type: 'textarea',
      name: 'Textarea',
      icon: () => <MdTextFields />,
      fields: () => [...baseFields]
    },
    {
      type: 'number',
      name: 'Number',
      icon: () => <MdLooks5 />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'number';
        extendedBaseFields[3].step = 'any';
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Data Type',
          key: 'step',
          defaultValue: null,
          options: [
            {
              value: '1',
              label: 'Integer'
            },
            {
              value: '0.1',
              label: 'Decimal (0.0)'
            },
            {
              value: '0.01',
              label: 'Decimal (0.00)'
            },
            {
              value: '0.001',
              label: 'Decimal (0.000)'
            },
            {
              value: '0.0001',
              label: 'Decimal (0.0000)'
            },
            {
              value: '0.00001',
              label: 'Decimal (0.00000)'
            }
          ],
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'radio',
      name: 'Radio',
      icon: () => <MdRadioButtonChecked />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields.splice(4, 0, {
          type: 'repeater',
          title: 'Options',
          key: 'options',
          layout: 'table',
          options: [
            {
              key: 'value',
              title: 'Value',
              type: 'text'
            },
            {
              key: 'label',
              title: 'Label',
              type: 'text'
            }
          ],
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'select',
      name: 'Select',
      icon: () => <MdArrowDropDown />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        Object.assign(extendedBaseFields[3], {
          type: 'switch',
          key: 'multiple',
          title: 'Allow multiple value selection'
        });
        extendedBaseFields.splice(4, 0, {
          type: 'repeater',
          title: 'Options',
          key: 'options',
          layout: 'table',
          options: [
            {
              key: 'value',
              title: 'Value',
              type: 'text'
            },
            {
              key: 'label',
              title: 'Label',
              type: 'text'
            }
          ],
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          },
          constraints: {
            presence: {
              message: '^At least one option is required',
              allowEmpty: false
            }
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'range',
      name: 'Range',
      icon: () => <MdTune />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[3].type = 'number';
        extendedBaseFields.splice(4, 0, {
          type: 'number',
          title: 'Min',
          key: 'min',
          defaultValue: 0,
          size: {
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6
          },
          constraints: {
            presence: {
              message: '^Min value is required'
            }
          }
        });
        extendedBaseFields.splice(5, 0, {
          type: 'number',
          title: 'Max',
          key: 'max',
          defaultValue: 999,
          size: {
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6
          }
        });
        extendedBaseFields.splice(6, 0, {
          type: 'number',
          title: 'Step Size',
          key: 'step',
          defaultValue: 1,
          size: {
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6
          }
        });
        extendedBaseFields.splice(7, 0, {
          type: 'text',
          title: 'Unit of measure',
          key: 'unit',
          defaultValue: '',
          size: {
            xs: 12,
            sm: 6,
            md: 6,
            lg: 6
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'select-relational',
      name: 'Select (Relational)',
      icon: () => <MdArrowDropDown />,
      fields({ props, editing = {} }) {
        const extendedBaseFields = cloneDeep(baseFields);
        const SUPPORTED_TYPES = [
          'boolean',
          'checkbox',
          'color',
          'date',
          'datetime-local',
          'email',
          'linked-text',
          'number',
          'password',
          'radio',
          'range',
          'select',
          'text',
          'textarea',
          'time'
        ];
        Object.assign(extendedBaseFields[3], {
          type: 'switch',
          key: 'multiple',
          title: 'Allow multiple value selection'
        });
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Relational Data',
          key: 'relation',
          options: map(
            filter(
              get(
                props,
                `firebase.environments.${props.environment}.schemas`,
                {}
              ),
              schema => schema.enabled
            ),
            ({ id: value, title: label }) => ({ value, label })
          ),
          size: {
            xs: 12,
            sm: 12,
            md: 12
          },
          constraints: {
            presence: {
              message: '^Relation is required',
              allowEmpty: false
            }
          }
        });
        extendedBaseFields.splice(5, 0, {
          type: 'select',
          title: 'Separator',
          key: 'fieldSeparator',
          multiple: false,
          defaultValue: DEFAULT_SEPARATOR,
          options: SUPPORTED_SEPARATORS.map(separator => ({
            value: separator,
            label: separator
          })),
          constraints: {
            presence: {
              message: '^Need to select a separator',
              allowEmpty: false
            }
          },
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        extendedBaseFields.splice(5, 0, {
          type: 'select',
          title: 'Fields to show',
          key: 'relationalFieldsToShow',
          multiple: true,
          defaultValue: [],
          options: without(
            map(
              get(
                props,
                `firebase.environments.${props.environment}.schemas.${
                  editing.relation
                }.fields`,
                []
              ),
              ({ key: value, title: label, type }) =>
                SUPPORTED_TYPES.includes(type) ? { value, label } : false
            ),
            false
          ),
          constraints: {
            presence: {
              message: '^Need to select at least one field',
              allowEmpty: false
            }
          },
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'tree-relational',
      name: 'Tree (Relational)',
      icon: () => <MdClearAll />,
      fields({ props, editing = {} }) {
        const extendedBaseFields = cloneDeep(baseFields);
        const SUPPORTED_TYPES = [
          'boolean',
          'checkbox',
          'color',
          'date',
          'datetime-local',
          'email',
          'linked-text',
          'number',
          'password',
          'radio',
          'range',
          'select',
          'text',
          'textarea',
          'time'
        ];
        extendedBaseFields.splice(3, 1, {
          type: 'select',
          title: 'Relational Data',
          key: 'relation',
          options: map(
            filter(
              get(
                props,
                `firebase.environments.${props.environment}.schemas`,
                {}
              ),
              schema => schema.enabled
            ),
            ({ id: value, title: label }) => ({ value, label })
          ),
          size: {
            xs: 12,
            sm: 12,
            md: 12
          },
          constraints: {
            presence: {
              message: '^Relation is required',
              allowEmpty: false
            }
          }
        });
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Separator',
          key: 'fieldSeparator',
          multiple: false,
          defaultValue: DEFAULT_SEPARATOR,
          options: SUPPORTED_SEPARATORS.map(separator => ({
            value: separator,
            label: separator
          })),
          constraints: {
            presence: {
              message: '^Need to select a separator',
              allowEmpty: false
            }
          },
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        extendedBaseFields.splice(4, 0, {
          type: 'select',
          title: 'Fields to show',
          key: 'relationalFieldsToShow',
          multiple: true,
          defaultValue: [],
          options: without(
            map(
              get(
                props,
                `firebase.environments.${props.environment}.schemas.${
                  editing.relation
                }.fields`,
                []
              ),
              ({ key: value, title: label, type }) =>
                SUPPORTED_TYPES.includes(type) ? { value, label } : false
            ),
            false
          ),
          constraints: {
            presence: {
              message: '^Need to select at least one field',
              allowEmpty: false
            }
          },
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        });
        return [...extendedBaseFields];
      }
    },
    {
      type: 'repeater',
      name: 'Repeater',
      icon: () => <MdRepeat />,
      fields({ state }) {
        const extendedBaseFields = cloneDeep(baseFields);
        const showInOverviewSelector = cloneDeep(extendedBaseFields[8]);
        showInOverviewSelector.size = {
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6
        };

        extendedBaseFields.splice(3, 1);
        extendedBaseFields[2].size = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12
        };
        extendedBaseFields.splice(7, 2, {
          type: 'select',
          title: 'Field Layout',
          key: 'layout',
          defaultValue: 'table',
          size: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          },
          constraints: {
            presence: {
              message: '^Field layout is required'
            }
          },
          options: [
            {
              value: 'table',
              label: 'Table'
            },
            {
              value: 'list',
              label: 'List'
            }
          ]
        });

        extendedBaseFields.push(showInOverviewSelector);

        if (get(state, 'editing.show')) {
          extendedBaseFields.push(
            {
              type: 'select',
              title: 'Overview Fields',
              key: 'overviewFields',
              defaultValue: [],
              multiple: true,
              size: {
                xs: 12,
                sm: 12,
                md: 6,
                lg: 6
              },
              constraints: {
                presence: {
                  message: '^Field to show is required'
                }
              },
              options: get(state, 'editing.options', []).map(option => ({
                value: option.key,
                label: option.title
              }))
            },
            {
              type: 'select',
              title: 'Overview Fields Separator',
              key: 'overviewFieldsSeparator',
              multiple: false,
              defaultValue: DEFAULT_SEPARATOR,
              options: SUPPORTED_SEPARATORS.map(separator => ({
                value: separator,
                label: separator
              })),
              constraints: {
                presence: {
                  message: '^Need to select a separator',
                  allowEmpty: false
                }
              },
              size: {
                xs: 12,
                sm: 12,
                md: 6,
                lg: 6
              }
            }
          );
        }

        return [...extendedBaseFields];
      }
    },
    {
      type: 'fieldset',
      name: 'Field Group',
      icon: () => <MdDehaze />,
      fields({ state }) {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[2].size = 12;
        extendedBaseFields.splice(3, 1);
        extendedBaseFields.splice(8, 1);
        extendedBaseFields[7].size = {
          xs: 12,
          sm: 12,
          md: 6,
          lg: 6
        };

        if (get(state, 'editing.show')) {
          extendedBaseFields.push(
            {
              type: 'select',
              title: 'Overview Fields',
              key: 'overviewFields',
              multiple: true,
              defaultValue: [],
              size: {
                xs: 12,
                sm: 12,
                md: 6,
                lg: 6
              },
              constraints: {
                presence: {
                  message: '^Field to show is required'
                }
              },
              options: get(state, 'editing.options', []).map(option => ({
                value: option.key,
                label: option.title
              }))
            },
            {
              type: 'select',
              title: 'Overview Fields Separator',
              key: 'overviewFieldsSeparator',
              multiple: false,
              defaultValue: DEFAULT_SEPARATOR,
              options: SUPPORTED_SEPARATORS.map(separator => ({
                value: separator,
                label: separator
              })),
              constraints: {
                presence: {
                  message: '^Need to select a separator',
                  allowEmpty: false
                }
              },
              size: {
                xs: 12,
                sm: 12,
                md: 6,
                lg: 6
              }
            }
          );
        }

        return [...extendedBaseFields];
      }
    },
    {
      type: 'wysiwyg',
      name: 'WYSIWYG',
      icon: () => <MdTextFormat />,
      fields: () => [...baseFields]
    },
    {
      type: 'wysiwyg-cke',
      name: 'WYSIWYG (CKEditor)',
      icon: () => <MdTextFormat />,
      fields: () => [...baseFields]
    },
    {
      type: 'markdown-editor',
      name: 'Markdown Editor',
      icon: () => <MdTextFormat />,
      fields: () => [...baseFields]
    }
  ]
];

export const groupFields = [
  [
    {
      type: 'seo',
      groupType: 'fieldset',
      name: 'SEO',
      icon: () => <MdStar />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[0].defaultValue = 'Seo';
        extendedBaseFields[1].defaultValue = 'seo';
        return [...extendedBaseFields];
      },
      formFields: [
        {
          type: 'text',
          title: 'Meta Title',
          key: 'metaTitle',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Author',
          key: 'metaAuthor',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Canonical URL',
          key: 'canonicalUrl',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'textarea',
          title: 'Meta Description',
          key: 'metaDescription',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Open Graph Title',
          key: 'ogTitle',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Open Graph URL',
          key: 'ogUrl',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Open Graph Image',
          key: 'ogImage',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Open Graph Type',
          key: 'ogType',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Facebook Admin',
          key: 'fbAdmin',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'textarea',
          title: 'Open Graph Description',
          key: 'ogDescription',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Twitter Title',
          key: 'twitterTitle',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Twitter Url',
          key: 'twitterUrl',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Twitter Card',
          key: 'twitterCard',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Twitter Image',
          key: 'twitterImage',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Twitter Description',
          key: 'twitterDescription',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        }
      ]
    },
    {
      type: 'imageDeck',
      groupType: 'repeater',
      name: 'Image Deck',
      icon: () => <MdImage />,
      fields() {
        const extendedBaseFields = cloneDeep(baseFields);
        extendedBaseFields[0].defaultValue = 'Image Deck';
        extendedBaseFields[1].defaultValue = 'imageDeck';
        return [...extendedBaseFields];
      },
      formFields: [
        {
          type: 'media',
          title: 'Image',
          key: 'image',
          mediaTypes: ['images'],
          defaultValue: '',
          limit: 1,
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Title',
          key: 'title',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Description',
          key: 'description',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        }
      ]
    }
  ],
  [
    {
      type: 'blog',
      groupType: false,
      name: 'Blog',
      icon: () => <MdEdit />,
      fields: () => [...baseFields],
      formFields: [
        {
          type: 'text',
          title: 'Title',
          key: 'title',
          show: true,
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'textarea',
          title: 'Summary',
          key: 'summary',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'text',
          title: 'Author',
          key: 'author',
          show: true,
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          }
        },
        {
          type: 'date',
          title: 'Date',
          key: 'date',
          show: true,
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 6,
            lg: 6
          }
        },
        {
          type: 'wysiwyg-cke',
          title: 'Content',
          key: 'content',
          defaultValue: '',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: 12
          }
        },
        {
          type: 'select',
          title: 'Status',
          key: 'status',
          show: true,
          defaultValue: 'draft',
          gridColumns: {
            xs: 12,
            sm: 12,
            md: 4,
            lg: 4
          },
          options: [
            {
              value: 'draft',
              label: 'Draft'
            },
            {
              value: 'published',
              label: 'Published'
            }
          ]
        }
      ]
    }
  ]
];

export const schemaFields = state => {
  const fields = [
    {
      type: 'text',
      title: 'Title',
      key: 'title',
      defaultValue: '',
      constraints: {
        presence: {
          message: '^Title is required',
          allowEmpty: false
        }
      },
      size: {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12
      }
    },
    {
      type: 'linked-text',
      title: 'Schema ID',
      key: 'id',
      linkedField: 'title',
      transformFunction: 'camelCase',
      manualOverwrite: true,
      overwriteOnceSet: isNew => isNew === 'new',
      defaultValue: '',
      constraints: {
        presence: {
          message: '^Schema ID is required',
          allowEmpty: false
        },
        format: {
          pattern: '[a-z0-9_-]+',
          flags: 'i',
          message: '^Can only be a valid slug'
        }
      },
      size: {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12
      }
    },
    {
      type: 'text',
      title: 'Description',
      key: 'description',
      defaultValue: '',
      size: {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12
      }
    },
    {
      type: 'select',
      title: 'Type',
      key: 'type',
      defaultValue: 'collection',
      constraints: {
        presence: {
          message: '^Schema type is required',
          allowEmpty: false
        }
      },
      size: {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12
      },
      options: [
        {
          value: 'single',
          label: 'Single'
        },
        {
          value: 'collection',
          label: 'Collection'
        },
        {
          value: 'form',
          label: 'Form'
        }
      ]
    },
    {
      type: 'boolean',
      title: 'Enabled',
      key: 'enabled',
      defaultValue: true,
      size: {
        xs: 12,
        sm: 12,
        md: 6,
        lg: 4
      }
    }
  ];

  if (get(state, 'formData.type') === 'single') {
    fields.splice(4, 0, {
      type: 'text',
      title: 'Group',
      key: 'group',
      description: 'Groups are used to group "Single" types together',
      defaultValue: '',
      size: {
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12
      }
    });
  }

  if (get(state, 'formData.type') === 'collection') {
    fields.splice(fields.length - 1, 0, {
      type: 'boolean',
      title: 'Sortable',
      key: 'sortable',
      defaultValue: true,
      size: {
        xs: 12,
        sm: 12,
        md: 6,
        lg: 4
      }
    });
  }

  return fields;
};

export const schemaFieldsConstraints = reduce(
  schemaFields,
  (rules, field) => assign({}, rules, { [field.key]: field.constraints || {} }),
  {}
);

export const getConstraints = myFormFields => {
  const fields = flatten(
    myFormFields.map(field => field.fields({ props: {} }))
  );
  return reduce(
    fields,
    (rules, field) => {
      if (field.constraints) {
        return assign({}, rules, { [field.key]: field.constraints });
      }
      return rules;
    },
    {}
  );
};

export const formFieldsConstraints = getConstraints(flatten(formFields));
